interface CachedUserData {
  email: string
  marianaTekData: any
  barrysPlatformData: any
  shopifyData: any
  memberships?: any[]
  classPackages?: any[]
  credits?: any[]
  reservations?: any
  referralCode?: string | null
  timestamp: number
}

class UserDataCache {
  private static instance: UserDataCache
  private cache: Map<string, CachedUserData> = new Map()
  private readonly CACHE_DURATION = 5 * 60 * 1000 // 5 minutes in milliseconds

  private constructor() {}

  public static getInstance(): UserDataCache {
    if (!UserDataCache.instance) {
      UserDataCache.instance = new UserDataCache()
    }
    return UserDataCache.instance
  }

  /**
   * Get cached data for a user email
   */
  get(email: string): CachedUserData | null {
    const cached = this.cache.get(email.toLowerCase())
    
    if (!cached) {
      return null
    }

    // Check if cache is expired
    if (Date.now() - cached.timestamp > this.CACHE_DURATION) {
      this.cache.delete(email.toLowerCase())
      return null
    }

    return cached
  }

  /**
   * Set cached data for a user email
   */
  set(email: string, data: Omit<CachedUserData, 'email' | 'timestamp'>): void {
    const cachedData: CachedUserData = {
      email: email.toLowerCase(),
      ...data,
      timestamp: Date.now()
    }
    
    this.cache.set(email.toLowerCase(), cachedData)
  }

  /**
   * Update specific data type for a user
   */
  updateMarianaTekData(email: string, marianaTekData: any): void {
    const existing = this.get(email)
    if (existing) {
      this.set(email, {
        marianaTekData,
        barrysPlatformData: existing.barrysPlatformData,
        shopifyData: existing.shopifyData,
        memberships: existing.memberships,
        classPackages: existing.classPackages,
        reservations: existing.reservations,
        referralCode: existing.referralCode
      })
    }
  }

  updateBarrysPlatformData(email: string, barrysPlatformData: any): void {
    const existing = this.get(email)
    if (existing) {
      this.set(email, {
        marianaTekData: existing.marianaTekData,
        barrysPlatformData,
        shopifyData: existing.shopifyData,
        memberships: existing.memberships,
        classPackages: existing.classPackages,
        reservations: existing.reservations,
        referralCode: existing.referralCode
      })
    }
  }

  updateShopifyData(email: string, shopifyData: any): void {
    const existing = this.get(email)
    if (existing) {
      this.set(email, {
        marianaTekData: existing.marianaTekData,
        barrysPlatformData: existing.barrysPlatformData,
        shopifyData,
        memberships: existing.memberships,
        classPackages: existing.classPackages,
        reservations: existing.reservations,
        referralCode: existing.referralCode
      })
    }
  }

  /**
   * Clear cache for a specific user
   */
  clearUser(email: string): void {
    this.cache.delete(email.toLowerCase())
  }

  /**
   * Clear all cached data
   */
  clearAll(): void {
    this.cache.clear()
  }

  /**
   * Get cache size for debugging
   */
  getSize(): number {
    return this.cache.size
  }

  /**
   * Get all cached emails for debugging
   */
  getCachedEmails(): string[] {
    return Array.from(this.cache.keys())
  }
}

export default UserDataCache